import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CpuChipIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  DocumentCheckIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';

const TrainAgent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [instructionsManuallyEdited, setInstructionsManuallyEdited] = useState(false);
  const [maxSystemPromptTokens, setMaxSystemPromptTokens] = useState(2000);

  const [verificationStatus, setVerificationStatus] = useState(null);
  const [checkingVerification, setCheckingVerification] = useState(true);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);

  // Website training states
  const [websiteTrainingEnabled, setWebsiteTrainingEnabled] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteVerificationStatus, setWebsiteVerificationStatus] = useState(null);
  const [websiteVerificationError, setWebsiteVerificationError] = useState('');

  // FAQ training states
  const [faqTrainingEnabled, setFaqTrainingEnabled] = useState(false);
  const [faqPairs, setFaqPairs] = useState([{ question: '', answer: '', id: Date.now() }]);

  // Document limits state
  const [documentLimits, setDocumentLimits] = useState({
    maxDocumentSizeBytes: 4194304, // 4MB default (scale package)
    maxTextLength: 4000, // 4K default (scale package)
    maxTokensPerDocument: 1000 // 1K default (scale package)
  });
  const [loadingLimits, setLoadingLimits] = useState(true);

  // FAQ helper functions
  const addFaqPair = () => {
    setFaqPairs(prev => [...prev, { question: '', answer: '', id: Date.now() }]);
  };

  const removeFaqPair = (id) => {
    setFaqPairs(prev => prev.filter(pair => pair.id !== id));
  };

  const updateFaqPair = (id, field, value) => {
    setFaqPairs(prev => prev.map(pair =>
      pair.id === id ? { ...pair, [field]: value } : pair
    ));
  };

  const [formData, setFormData] = useState({
    name: '',
    instructions: 'You are Snwolley, a helpful AI assistant. Respond to messages in a friendly and professional manner. Keep responses concise and relevant.',
    model: 'snwolley-2.0-alpha',
    temperature: 0.7,
    max_tokens: 1500,
    knowledge_base_enabled: false,
    auto_reply_enabled: true,
    response_delay_ms: 1000,
    typing_simulation: true,
    website_url: ''
  });

  // Fetch system settings
  const fetchSystemSettings = async () => {
    try {
      const authToken = localStorage.getItem('snwolley_auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/system/settings`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings.max_system_prompt_tokens) {
          setMaxSystemPromptTokens(data.settings.max_system_prompt_tokens);
        }
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };

  // Check verification status and fetch document limits on component mount
  useEffect(() => {
    const checkVerificationStatus = async () => {
      try {
        const response = await api.getVerificationStatus();
        setVerificationStatus(response.data.verification_status);
      } catch (error) {
        console.error('Error checking verification status:', error);
        setVerificationStatus('unverified');
      } finally {
        setCheckingVerification(false);
      }
    };

    const fetchDocumentLimits = async () => {
      try {
        // Get user's organization ID from localStorage
        const organizationId = localStorage.getItem('organization_id');
        if (organizationId) {
          // Call API to get package limits
          const response = await api.getPackageLimits(organizationId);
          console.log('Package limits API response:', response);

          if (response.data && response.data.limits) {
            setDocumentLimits({
              maxDocumentSizeBytes: response.data.limits.find(l => l.limit_type === 'max_document_size_bytes')?.limit_value || 4194304,
              maxTextLength: response.data.limits.find(l => l.limit_type === 'max_text_length')?.limit_value || 4000,
              maxTokensPerDocument: response.data.limits.find(l => l.limit_type === 'max_tokens_per_document')?.limit_value || 1000
            });
          } else {
            console.warn('No limits data in API response, using defaults');
            setDocumentLimits({
              maxDocumentSizeBytes: 4194304,
              maxTextLength: 4000,
              maxTokensPerDocument: 1000
            });
          }
        } else {
          console.warn('No organization ID found, using defaults');
          setDocumentLimits({
            maxDocumentSizeBytes: 4194304,
            maxTextLength: 4000,
            maxTokensPerDocument: 1000
          });
        }
      } catch (error) {
        console.error('Error fetching package limits:', error);
        setDocumentLimits({
          maxDocumentSizeBytes: 4194304,
          maxTextLength: 4000,
          maxTokensPerDocument: 1000
        });
      } finally {
        setLoadingLimits(false);
      }
    };

    checkVerificationStatus();
    fetchDocumentLimits();
    fetchSystemSettings();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (window.websiteVerifyTimeout) {
        clearTimeout(window.websiteVerifyTimeout);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked :
          type === 'number' ? parseFloat(value) || 0 : value
      };

      // Update instructions dynamically when agent name changes
      if (name === 'name' && !instructionsManuallyEdited) {
        const agentName = value.trim() || 'Snwolley';
        newData.instructions = `You are ${agentName}, a helpful AI assistant. Respond to messages in a friendly and professional manner. Keep responses concise and relevant.`;
      }

      // Track if instructions were manually edited
      if (name === 'instructions') {
        const expectedInstruction = `You are ${prev.name.trim() || 'Snwolley'}, a helpful AI assistant. Respond to messages in a friendly and professional manner. Keep responses concise and relevant.`;
        if (value !== expectedInstruction) {
          setInstructionsManuallyEdited(true);
        }
      }

      return newData;
    });
  };

  // Document upload handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const validFiles = Array.from(files).filter(file => {
      const validTypes = [
        'application/pdf',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/csv',
        'application/json'
      ];

      if (!validTypes.includes(file.type)) {
        toast.error(`File type ${file.type} is not supported. Please upload PDF, TXT, DOC, DOC, DOCX, CSV, or JSON files.`);
        return false;
      }

      const maxSizeMB = Math.floor(documentLimits.maxDocumentSizeBytes / (1024 * 1024));
      if (file.size > documentLimits.maxDocumentSizeBytes) {
        toast.error(`File ${file.name} is too large. Maximum size is ${maxSizeMB}MB.`);
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      const newDocuments = validFiles.map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'ready'
      }));

      setDocuments(prev => [...prev, ...newDocuments]);

      // Clear the file input to allow re-uploading the same file
      const fileInput = document.getElementById('file-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const removeDocument = (documentId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check verification status before proceeding
    if (verificationStatus !== 'verified') {
      setShowVerificationPrompt(true);
      return;
    }

    if (!formData.name.trim() || !formData.instructions.trim()) {
      toast.error('Agent name and instructions are required');
      return;
    }

    // Validate system prompt token limit (frontend check)
    const instructionsLength = formData.instructions.trim().length;
    const estimatedTokens = Math.ceil(instructionsLength / 4); // Rough estimation: 4 chars per token
    const maxTokens = maxSystemPromptTokens; // Use dynamic value from backend
    
    if (estimatedTokens > maxTokens) {
      toast.error(`System prompt too long (${estimatedTokens.toLocaleString()} tokens). Maximum allowed: ${maxTokens.toLocaleString()} tokens. Please reduce the length.`, {
        duration: 6000
      });
      return;
    }

    // Validate documents if knowledge base is enabled
    if (formData.knowledge_base_enabled && documents.length > 0) {
      const invalidDocuments = documents.filter(doc => !doc.file || doc.status === 'error');
      if (invalidDocuments.length > 0) {
        toast.error('Some documents are invalid. Please remove them and try again.');
        return;
      }
    }

    // Validate FAQ pairs if FAQ training is enabled
    if (faqTrainingEnabled) {
      const validFaqPairs = faqPairs.filter(pair => pair.question.trim() && pair.answer.trim());
      if (validFaqPairs.length === 0) {
        toast.error('Please add at least one complete FAQ pair or disable FAQ training.');
        return;
      }
    }

    try {
      setLoading(true);

      // Prepare agent data with FAQ if enabled
      const agentData = {
        ...formData,
        faq_enabled: faqTrainingEnabled,
        faq_pairs: faqTrainingEnabled ? faqPairs.filter(pair => pair.question.trim() && pair.answer.trim()) : [],
        website_training_enabled: websiteTrainingEnabled,
        website_url: websiteTrainingEnabled ? websiteUrl.trim() : ''
      };

      // Step 1: Create the agent
      console.log('Creating agent with data:', agentData);
      const response = await api.createAgent(agentData);

      if (!response.data || !response.data.agent || !response.data.agent.id) {
        throw new Error('Invalid response from server when creating agent');
      }

      const agentId = response.data.agent.id;

      // Step 2: Upload documents if knowledge base is enabled and documents are selected
      if (formData.knowledge_base_enabled && documents.length > 0) {
        console.log('Uploading documents...');
        setUploadingDocuments(true);

        // Update document status to uploading
        setDocuments(prev => prev.map(doc => ({ ...doc, status: 'uploading' })));

        try {
          // Create FormData for file upload
          const uploadFormData = new FormData();

          // Append each file with error handling
          documents.forEach((doc, index) => {
            if (doc.file && doc.file instanceof File) {
              uploadFormData.append('files', doc.file);
            } else {
              console.warn(`Document at index ${index} is not a valid File object:`, doc);
            }
          });

          // Only upload if we have valid files
          const validFileCount = documents.filter(doc => doc.file && doc.file instanceof File).length;
          if (validFileCount > 0) {
            await api.uploadAgentDocuments(agentId, uploadFormData);
            console.log('Documents uploaded successfully');

            // Update document status to uploaded
            setDocuments(prev => prev.map(doc => ({ ...doc, status: 'uploaded' })));
          } else {
            console.warn('No valid files to upload');
          }
        } catch (uploadError) {
          console.error('Error uploading documents:', uploadError);

          // Handle backend validation errors with detailed messages
          if (uploadError.response?.status === 413) {
            // Show the general error first
            if (uploadError.response.data?.error) {
              toast.error(uploadError.response.data.error, {
                duration: 6000
              });
            }

            // Show the detailed message if available
            if (uploadError.response.data?.message) {
              toast.error(uploadError.response.data.message, {
                duration: 8000 // Longer duration for detailed error messages
              });
            }
          } else {
            // Fallback to generic error handling
            toast.warning('Agent created but some documents failed to upload');
          }

          setDocuments(prev => prev.map(doc => ({ ...doc, status: 'error' })));
        } finally {
          setUploadingDocuments(false);
        }
      }

      toast.success('AI Agent trained successfully!');
      navigate(`/dashboard/agents/${agentId}`);

    } catch (error) {
      console.error('Error in agent creation process:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create agent';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Website verification function
  const verifyWebsite = async (url) => {
    setWebsiteVerificationStatus('verifying');
    setWebsiteVerificationError('');

    try {
      const response = await api.verifyWebsite(url);

      if (response.data.verified) {
        setWebsiteVerificationStatus('success');
        setFormData(prev => ({ ...prev, website_url: url }));
        toast.success('Website verified successfully!');
      } else {
        setWebsiteVerificationStatus('error');
        setWebsiteVerificationError(response.data.error || 'Website verification failed');
        toast.error(`Website verification failed: ${response.data.error}`);
      }
    } catch (error) {
      setWebsiteVerificationStatus('error');
      setWebsiteVerificationError('Failed to verify website');
      toast.error('Failed to verify website');
    }
  };

  // Handle website URL input change
  const handleWebsiteUrlChange = (e) => {
    const url = e.target.value;
    setWebsiteUrl(url);
    setWebsiteVerificationStatus(null);
    setWebsiteVerificationError('');

    // Auto-verify after user stops typing (debounce)
    if (url.trim()) {
      clearTimeout(window.websiteVerifyTimeout);
      window.websiteVerifyTimeout = setTimeout(() => {
        verifyWebsite(url.trim());
      }, 1000);
    }
  };


  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/dashboard/agents')}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Agents
        </button>
        <div>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DocumentTextIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Train AI Agent</h1>
              <p className="text-sm text-gray-600 mt-1">Upload knowledge documents and teach your agent</p>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Prompt Modal */}
      {showVerificationPrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Account Verification Required</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  To create AI agents, you need to verify your account with valid identification documents.
                  This ensures security and compliance with platform requirements.
                </p>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start">
                    <DocumentCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-xs text-blue-700">
                      <p className="font-medium">What you'll need:</p>
                      <ul className="mt-1 list-disc list-inside">
                        <li>Valid Ghana ID (National ID, Driver's License, SSNIT, or Ghana Card)</li>
                        <li>Clear photos of your documents</li>
                        <li>Basic business information</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="items-center px-4 py-3">
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => navigate('/dashboard/verification')}
                    className="px-4 py-2 bg-whatsapp-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-whatsapp-600 focus:outline-none focus:ring-2 focus:ring-whatsapp-300"
                  >
                    Start Verification Process
                  </button>
                  <button
                    onClick={() => setShowVerificationPrompt(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Status Banner */}
      {!checkingVerification && verificationStatus !== 'verified' && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Account Verification Pending</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Your account needs to be verified before you can create AI agents.
                <button
                  onClick={() => navigate('/dashboard/verification')}
                  className="ml-1 font-medium underline hover:text-yellow-900"
                >
                  Complete verification now
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {checkingVerification && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
            <p className="text-sm text-blue-700">Checking account verification status...</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            <p className="text-sm text-gray-500 mt-1">
              Configure the basic details of your AI agent
            </p>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Agent Name *
              </label>
              <input
                type="text"
                name="name"
                id="name"
                required
                value={formData.name}
                onChange={handleInputChange}
                disabled={!checkingVerification && verificationStatus !== 'verified'}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="e.g., Customer Support Assistant"
              />
            </div>

            <div>
              <label htmlFor="instructions" className="block text-sm font-medium text-gray-700">
                System Instructions *
              </label>
              <div className="mt-1">
                <textarea
                  name="instructions"
                  id="instructions"
                  rows={6}
                  required
                  value={formData.instructions}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="Instructions will auto-update based on agent name..."
                />
              </div>
              {/* Token Counter */}
              <div className="mt-2 flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  These instructions define how your AI agent will behave and respond to messages.
                </p>
                <div className="text-sm">
                  {(() => {
                    const charCount = formData.instructions.length;
                    const estimatedTokens = Math.ceil(charCount / 4);
                    const maxTokens = maxSystemPromptTokens;
                    const isNearLimit = estimatedTokens > maxTokens * 0.8;
                    const isOverLimit = estimatedTokens > maxTokens;
                    
                    return (
                      <span className={`font-medium ${
                        isOverLimit ? 'text-red-600' :
                        isNearLimit ? 'text-amber-600' :
                        'text-gray-500'
                      }`}>
                        ~{estimatedTokens.toLocaleString()} / {maxTokens.toLocaleString()} tokens
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Behavior Settings */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Behavior Settings</h3>
            <p className="text-sm text-gray-500 mt-1">
              Configure how the agent behaves in conversations
            </p>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="response_delay_ms" className="block text-sm font-medium text-gray-700">
                  Response Delay (ms)
                </label>
                <input
                  type="number"
                  name="response_delay_ms"
                  id="response_delay_ms"
                  min="0"
                  max="10000"
                  step="100"
                  value={formData.response_delay_ms}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Base delay before responding (plus response token calculation time)
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="auto_reply_enabled"
                  name="auto_reply_enabled"
                  type="checkbox"
                  checked={formData.auto_reply_enabled}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="auto_reply_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                  Enable auto-reply
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="typing_simulation"
                  name="typing_simulation"
                  type="checkbox"
                  checked={formData.typing_simulation}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="typing_simulation" className="ml-3 block text-sm font-medium text-gray-700">
                  Simulate typing indicator
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="knowledge_base_enabled"
                  name="knowledge_base_enabled"
                  type="checkbox"
                  checked={formData.knowledge_base_enabled}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="knowledge_base_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                  Enable knowledge base
                </label>
                <div className="ml-2 group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    Upload documents to train your agent with specific knowledge
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="website_training_enabled"
                  name="website_training_enabled"
                  type="checkbox"
                  checked={websiteTrainingEnabled}
                  onChange={(e) => setWebsiteTrainingEnabled(e.target.checked)}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="website_training_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                  Train with website
                </label>
                <div className="ml-2 group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    Train your agent with content from your website
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="faq_training_enabled"
                  name="faq_training_enabled"
                  type="checkbox"
                  checked={faqTrainingEnabled}
                  onChange={(e) => setFaqTrainingEnabled(e.target.checked)}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="faq_training_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                  Train with FAQ
                </label>
                <div className="ml-2 group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    Add custom question-answer pairs to train your agent
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Knowledge Base Documents */}
        {formData.knowledge_base_enabled && (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Knowledge Base Documents</h3>
              <p className="text-sm text-gray-500 mt-1">
                Upload documents to train your agent with specific knowledge (PDF, TXT, DOC, DOCX, CSV, JSON)
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* File Upload Dropzone */}
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="text-center">
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Drop files here or click to upload
                      </span>
                      <span className="mt-1 block text-xs text-gray-500">
                        PDF, TXT, DOC, DOCX, CSV, JSON up to {Math.floor(documentLimits.maxDocumentSizeBytes / (1024 * 1024))}MB each
                      </span>
                    </label>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      multiple
                      accept=".pdf,.txt,.doc,.docx,.csv,.json"
                      className="sr-only"
                      onChange={handleFileInput}
                      disabled={!checkingVerification && verificationStatus !== 'verified'}
                    />
                  </div>
                </div>
              </div>

              {/* Uploaded Documents List */}
              {documents.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Uploaded Documents ({documents.length})</h4>
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(doc.size)} • {doc.type.split('/')[1].toUpperCase()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            doc.status === 'ready' ? 'bg-blue-100 text-blue-800' :
                              doc.status === 'uploading' ? 'bg-yellow-100 text-yellow-800' :
                                doc.status === 'uploaded' ? 'bg-green-100 text-green-800' :
                                  'bg-red-100 text-red-800'
                          }`}>
                            {doc.status === 'ready' ? 'Ready' :
                              doc.status === 'uploading' ? 'Uploading...' :
                                doc.status === 'uploaded' ? 'Uploaded' :
                                  'Error'}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeDocument(doc.id)}
                            className="text-gray-400 hover:text-red-500"
                            disabled={doc.status === 'uploading'}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Knowledge Base Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">Knowledge Base Training</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Documents will be processed and used to train your agent. The agent will be able to answer questions based on the content of these documents.
                    </p>
                    <div className="mt-2">
                      <h5 className="text-xs font-medium text-blue-800">Supported Formats:</h5>
                      <ul className="text-xs text-blue-700 mt-1 list-disc list-inside">
                        <li>PDF files (.pdf)</li>
                        <li>Word documents (.doc, .docx)</li>
                        <li>Plain text (.txt)</li>
                        <li>CSV files (.csv)</li>
                        <li>JSON files (.json)</li>
                      </ul>
                      <p className="text-xs text-blue-600 mt-1">
                        Maximum: {Math.floor(documentLimits.maxDocumentSizeBytes / (1024 * 1024))}MB per file, {documentLimits.maxTextLength.toLocaleString()} characters, {documentLimits.maxTokensPerDocument.toLocaleString()} tokens
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Website Training */}
        {websiteTrainingEnabled && (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Website Training</h3>
              <p className="text-sm text-gray-500 mt-1">
                Enter a website URL to train your agent with content from that website
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Website URL Input */}
              <div>
                <label htmlFor="website_url" className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={websiteUrl}
                    onChange={handleWebsiteUrlChange}
                    placeholder="https://example.com"
                    disabled={!checkingVerification && verificationStatus !== 'verified'}
                    className="block w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                  />
                  {/* Verification Status Icons */}
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {websiteVerificationStatus === 'verifying' && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    )}
                    {websiteVerificationStatus === 'success' && (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    )}
                    {websiteVerificationStatus === 'error' && (
                      <XCircleIcon className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </div>
                {/* Error Message */}
                {websiteVerificationStatus === 'error' && websiteVerificationError && (
                  <p className="mt-1 text-sm text-red-600">{websiteVerificationError}</p>
                )}
                {/* Success Message */}
                {websiteVerificationStatus === 'success' && (
                  <p className="mt-1 text-sm text-green-600">Website verified successfully!</p>
                )}
              </div>

              {/* Website Training Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-green-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-green-800">Website Content Training</h4>
                    <p className="mt-1 text-sm text-green-700">
                      Your agent will be trained with the content from the provided website. This allows the agent to answer questions based on the website's information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Training */}
        {faqTrainingEnabled && (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">FAQ Training</h3>
              <p className="text-sm text-gray-500 mt-1">
                Add question-answer pairs to train your agent with specific responses
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* FAQ Pairs */}
              <div className="space-y-4">
                {faqPairs.map((pair, index) => (
                  <div key={pair.id} className="border border-gray-200 rounded-lg p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-medium text-gray-700">FAQ Pair {index + 1}</h4>
                      {faqPairs.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFaqPair(pair.id)}
                          className="text-red-500 hover:text-red-700"
                          disabled={!checkingVerification && verificationStatus !== 'verified'}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Question
                      </label>
                      <textarea
                        rows={2}
                        value={pair.question}
                        onChange={(e) => updateFaqPair(pair.id, 'question', e.target.value)}
                        placeholder="Enter a question that users might ask..."
                        disabled={!checkingVerification && verificationStatus !== 'verified'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Answer
                      </label>
                      <textarea
                        rows={3}
                        value={pair.answer}
                        onChange={(e) => updateFaqPair(pair.id, 'answer', e.target.value)}
                        placeholder="Enter the response your agent should give..."
                        disabled={!checkingVerification && verificationStatus !== 'verified'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Add FAQ Pair Button */}
              <button
                type="button"
                onClick={addFaqPair}
                disabled={!checkingVerification && verificationStatus !== 'verified'}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="text-gray-600">
                  <DocumentTextIcon className="mx-auto h-8 w-8 mb-2" />
                  <span className="block text-sm font-medium">Add Another FAQ Pair</span>
                  <span className="block text-xs text-gray-500 mt-1">Click to add more questions and answers</span>
                </div>
              </button>

              {/* FAQ Training Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">FAQ Training</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Your agent will be trained with these question-answer pairs. When users ask similar questions, the agent will provide responses based on this training data.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard/agents')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || uploadingDocuments || (!checkingVerification && verificationStatus !== 'verified')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {uploadingDocuments ? 'Uploading Documents...' : 'Training Agent...'}
              </>
            ) : (
              <>
                <CpuChipIcon className="h-4 w-4 mr-2" />
                📚 Train AI Agent
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TrainAgent;